import {useState} from "react";
import moment from "moment";
import {Checkbox, FormControlLabel} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import MainActions from "@/components/MainActions";
import VLFTextField from "@/components/Input/TextField";
import {
	countUsefulInputs,
	MIN_USEFUL_INPUTS,
	profileSignature,
} from "@/utils/aiProfile";
import {countWords} from "@/utils/scoutingReports";

import "./index.scss";

const MAX_WORDS = 60;

// Profilo sintetico della scheda: si scrive a mano; "Genera" compare solo con la generazione lato server.
export default function AiProfileCard({
	profile,
	inputs,
	onGenerate,
	onSave,
	busy,
	outdated: serverOutdated,
	currentSignature,
	available = true,
}) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState("");
	const [base, setBase] = useState({});
	const [reviewCurrent, setReviewCurrent] = useState(false);
	const useful = countUsefulInputs(inputs);
	const canGenerate = useful >= MIN_USEFUL_INPUTS && !busy;
	const outdated =
		serverOutdated ??
		(profile?.text && profile.signature !== profileSignature(inputs));
	const words = countWords(draft);
	const startEditing = () => {
		setDraft(profile?.text || "");
		setBase({
			version: profile?.version || 0,
			expectedSignature: currentSignature,
		});
		setReviewCurrent(false);
		setEditing(true);
	};

	const actions = [
		...(onSave
			? [
					{
						icon: <EditIcon />,
						label: profile?.text ? "Modifica" : "Scrivi profilo",
						title: profile?.text ? "Modifica" : "Scrivi profilo",
						disabled: busy,
						onClick: startEditing,
					},
				]
			: []),
		...(onGenerate
			? [
					{
						icon: <AutoAwesomeIcon />,
						label: profile?.text ? "Rigenera" : "Genera",
						title: profile?.text ? "Rigenera" : "Genera",
						disabled: !canGenerate,
						onClick: onGenerate,
					},
				]
			: []),
	];

	return (
		<section className="ai-profile">
			<div className="ai-profile__head">
				<h3>Profilo sintetico</h3>
				{!editing && !!actions.length && <MainActions actions={actions} />}
			</div>

			{outdated && !editing && (
				<div className="ai-profile__warning" role="status">
					Ruoli, attitudini o morfologia sono cambiati da quando il profilo è
					stato scritto: {onGenerate ? "rigenera" : "rivedi"} il profilo.
				</div>
			)}

			{editing ? (
				<>
					<VLFTextField
						disabled={busy}
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						placeholder="2–3 frasi, con il registro di un tecnico"
						multiline
						minRows={3}
						maxRows={10}
						size="small"
						inputProps={{"aria-label": "Testo del profilo"}}
						error={words > MAX_WORDS}
						helperText={
							words + (words === 1 ? " parola" : " parole") + " su " + MAX_WORDS
						}
					/>
					{outdated && (
						<FormControlLabel
							control={
								<Checkbox
									checked={reviewCurrent}
									disabled={busy}
									onChange={(e) => setReviewCurrent(e.target.checked)}
								/>
							}
							label="Ho rivisto il profilo rispetto ai ruoli, alle attitudini e alla morfologia attuali"
						/>
					)}
					<div className="ai-profile__edit-actions">
						<MainActions
							actions={[
								{
									icon: <CloseIcon />,
									label: "Annulla",
									title: "Annulla",
									color: "info",
									disabled: busy,
									onClick: () => setEditing(false),
								},
								{
									icon: <CheckIcon />,
									label: "Conferma",
									title: "Conferma",
									disabled:
										!draft.trim() || words > MAX_WORDS || busy || !onSave,
									onClick: async () => {
										try {
											if (currentSignature)
												await onSave(draft.trim(), {...base, reviewCurrent});
											else await onSave(draft.trim());
											setEditing(false);
										} catch {
											/* Preserve the draft; the parent shows the error. */
										}
									},
								},
							]}
						/>
					</div>
				</>
			) : profile?.text ? (
				<>
					<p className="ai-profile__text">{profile.text}</p>
					<div className="ai-profile__meta">
						{[
							profile.edited ? "Scritto a mano" : "Generato",
							profile.author?.name,
							profile.updatedAt &&
								moment(profile.updatedAt).format("DD/MM/YYYY"),
						]
							.filter(Boolean)
							.join(" · ")}
					</div>
				</>
			) : available ? (
				<p className="ai-profile__empty">
					{!onSave && !onGenerate
						? "Nessun profilo sintetico condiviso."
						: !onGenerate
							? "Nessun profilo: descrivi il giocatore in 2–3 frasi (massimo 60 parole)."
							: useful >= MIN_USEFUL_INPUTS
								? "Nessun profilo: generalo dai dati della scheda."
								: "Servono almeno due informazioni (ruolo, piede, altezza, attitudini, visioni, note o statistiche) per generare il profilo."}
				</p>
			) : null}
		</section>
	);
}
